import { DEFAULT_CURRENCY } from 'core/constants/currency.constants';
import { CART_VALIDATION_ERRORS_ID } from 'core/constants/shopping-cart.constants';
import { EProductType } from 'core/model/enums/product.enum';
import { ECartError } from 'core/model/enums/shopping-cart.enum';
import { IAddedProduct, ICartProduct } from 'core/model/interfaces/product.interface';
import { IShoppingCart, IShoppingCartGroup } from 'core/model/interfaces/shopping-cart.interface';
import { EMPTY_SHOPPING_CART_GROUP } from 'core/store/cart/constants';
import { getPresentationTextForDetail } from 'shared/utils/products.utils';
import { ICurrencyDTO } from '../catalogs/catalogs.dto';
import {
  IGetShoppingCartDTO,
  IPutShoppingCartRequestParams,
  IShoppingCartGroupBySourceDTO,
  IShoppingCartItemDTO,
} from './shopping-cart.dto';

let currency: ICurrencyDTO = {
  code: DEFAULT_CURRENCY.id,
  symbol: DEFAULT_CURRENCY.symbol,
};

const mapShoppingCartItemToProduct = (item: IShoppingCartItemDTO, sourceId: EProductType): ICartProduct => {
  const { presentation } = item.product;

  const unitMeasure = getPresentationTextForDetail(presentation);

  return {
    amounts: {
      base: item.totals.base,
      final: item.totals.amount,
      igv: item.totals.igv,
      perception: item.totals.perception,
      price: item.totals.price,
      discount: item.totals.discount,
    },
    brand: item.product.brand.name,
    categoryId: Number(item.product.category.id),
    categoryName: item.product.category.name,
    currency: {
      id: currency.code,
      symbol: currency.symbol,
    },
    promos: item.promotions
      ? item.promotions.map(promo => ({
          id: promo.externalId,
          name: promo.description,
          value: promo.discount || undefined,
        }))
      : [],
    eanCode: item.product.ean,
    image: item.product.imageUrl,
    octagonsImageUrl: item.product.octagonsImageUrl || '',
    name: item.product.name,
    providerDisplayName: item.product.provider.name,
    quantity: item.requestedQuantity,
    sku: item.product.id,
    sourceId,
    stock: item.product.presentation.stock,
    subcategoryId: Number(item.product.family.id),
    subcategoryName: item.product.family.name,
    reducedQuantity: item.reducedQuantity || 0,
    taxRate: 0,
    unitMeasure: {
      originalPrice: item.product.price.original,
      price: item.product.price.amount,
      name: presentation.name,
      pluralName: presentation.pluralName,
      default: presentation.isBase,
      purchaseIncrement: presentation.purchaseIncrement ?? 1,
      promoIndicators: {
        hasDiscount: presentation.promotions.hasDiscount,
      },
      code: presentation.id,
      presentation: unitMeasure,
    },
    metadata: {
      index: item.metadata?.index ?? 1,
      itemListName: item.metadata?.itemListName,
      origin: item.metadata?.origin,
    },
    distributorName: item.product.distributor.name,
    distributorId: item.product.distributor.id,
  };
};

const mapItemsGroup = (itemsGroup?: IShoppingCartGroupBySourceDTO): IShoppingCartGroup => {
  if (!itemsGroup) return EMPTY_SHOPPING_CART_GROUP;
  return {
    products: itemsGroup.items
      ? itemsGroup.items.map(item => mapShoppingCartItemToProduct(item, itemsGroup.sourceId))
      : [],
    amounts: {
      ...itemsGroup.totals,
      price: itemsGroup.totals.price,
      final: itemsGroup.totals.amount,
    },
    messages: itemsGroup.messages || [],
  };
};

export const mapCartDTOToShoppingCart = (response: IGetShoppingCartDTO): IShoppingCart => {
  const carts = response?.carts || [];

  currency = response.currency;

  let alliedItems: IShoppingCartGroupBySourceDTO | undefined;
  let alicorpItems: IShoppingCartGroupBySourceDTO | undefined;
  let generalItems: IShoppingCartGroupBySourceDTO | undefined;

  if (carts.length > 1) {
    alliedItems = carts.find(cart => cart.sourceId === EProductType.ALLIED);
    alicorpItems = carts.find(cart => cart.sourceId === EProductType.ALICORP);
  } else {
    [generalItems] = carts;
  }

  const data: IShoppingCart = {
    isValid: response.isValid,
    deletedProducts: response.deletedItems
      ? response.deletedItems.map(dp => ({
          displayName: dp.product.name,
          sku: dp.id,
          image: dp.product.imageUrl,
          presentation: dp.product.presentation.pluralName,
        }))
      : [],
    amounts: {
      base: response.totals.base,
      final: response.totals.amount,
      igv: response.totals.igv,
      perception: response.totals.perception,
      price: response.totals.price,
      discount: response.totals.discount,
    },
    itemsGroups: {
      alliedItems: mapItemsGroup(alliedItems),
      alicorpItems: mapItemsGroup(alicorpItems),
      generalItems: mapItemsGroup(generalItems),
    },
    currency: DEFAULT_CURRENCY,
    errorType: getErrorType(response.carts),
  };
  return data;
};

const getErrorType = (carts: Array<IShoppingCartGroupBySourceDTO>) => {
  if (!carts || carts.length === 0) return null;

  const existCartWithUserValidationError = carts.some(cart =>
    cart.messages.some(message => CART_VALIDATION_ERRORS_ID.includes(message.id)),
  );

  return existCartWithUserValidationError ? ECartError.SHOW_USER_VALIDATION_ERROR : null;
};

export const mapChangeProductParamsToPutParams = (params: IAddedProduct): IPutShoppingCartRequestParams => ({
  externalProductId: params.sku,
  unitMeasureId: params.unitMeasureCode,
  suggestedProductTypeId: params.suggestedProductType,
  requestedQuantity: params.quantity,
  metadata: params.metadata,
});
